import ReactApexChart from "react-apexcharts";
import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import { Card } from "react-bootstrap";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import EmptyData from "../../../components/EmptyData";
import Currency, { ConvertCurrency } from "../../../helpers/Currency/Currency";
import Badge from "../../../components/Badge";
import UserAvatar from "../../../components/UserAvatar";
import VerifiedBadge from "../../../components/VerifiedBadge";
import { dashboardUserContext } from "../../../context/UserContext/DashboardUserContext/DashboardUserContext";
import {
  convertDate,
  generateRandomHexColor,
  numberSeparate,
} from "../../../utils";
import {
  convertInvoiceStatusToColor,
  convertInvoiceStatusToText,
  convertOrderStatusToColor,
  convertOrderStatusToText,
  convertProductTypeToText,
  convertRoleToPath,
  convertRoleToText,
  convertTransactionTypeToColor,
  convertTransactionTypeToIcon,
  convertTransactionTypeToText,
  invoiceStatusItems,
  productTypeItems,
  userRoleItems,
} from "../../../utils/enum";

const DashboardUser = () => {
  const auth = useSelector((state) => state.auth);
  const permissions = useSelector((state) => state.permissions);

  const { overview, handleGetOverview } = useContext(dashboardUserContext);

  useEffect(() => {
    handleGetOverview();
  }, []);

  if (isEmpty(overview))
    return (
      <>
        <SiteTitle title="خانه" />

        <Card body>
          <EmptyData />
        </Card>
      </>
    );

  return (
    <>
      <SiteTitle title="خانه" />

      <section className="row gy-4">
        <TotalReport />
        {permissions.canInvoices && <RecentInvoices />}

        {auth.isAdmin && permissions.canProducts && <ProductStatistics />}

        {auth.isAdmin && permissions.canProducts && <RecentProducts />}

        {permissions.canOrders && <RecentOrders />}

        {auth.isAdmin && permissions.canUsers && <RecentUsers />}

        {auth.isAdmin && permissions.canUsers && <UserStatistics />}

        {permissions.canTransactions && <RecentTransactions />}
      </section>
    </>
  );
};

export default DashboardUser;

const TotalReport = () => {
  const auth = useSelector((state) => state.auth);
  const permissions = useSelector((state) => state.permissions);

  const { overview } = useContext(dashboardUserContext);

  const items = [
    auth.isAdmin &&
      permissions.canUsers && {
        title: "کاربران",
        icon: "solar:users-group-two-rounded-broken",
        color: "primary",
        key: "user",
        link: "/users",
      },
    auth.isAdmin &&
      permissions.canProducts && {
        title: "محصولات",
        icon: "solar:box-broken",
        color: "secondary",
        key: "product",
        link: "/products",
      },
    permissions.canTickets && {
      title: "پشتیبانی",
      icon: "solar:chat-round-call-broken",
      color: "success",
      key: "ticket",
      link: "/tickets",
    },
    permissions.canOrders && {
      title: "سفارشات",
      icon: "solar:reorder-broken",
      color: "info",
      key: "order",
      link: "/orders",
    },
    auth.isAdmin &&
      permissions.canDiscountCodes && {
        title: "کدهای تخفیف",
        icon: "solar:wad-of-money-broken",
        color: "warning",
        key: "discountCode",
        link: "/discount-code",
      },
    permissions.canInvoices && {
      title: "فاکتورها",
      icon: "solar:document-text-broken",
      color: "danger",
      key: "invoice",
      link: "/invoices",
    },
    permissions.canRenewals && {
      title: "تمدیدها",
      icon: "solar:repeat-one-broken",
      color: "dark",
      key: "renewal",
      link: "/renewals",
    },
    permissions.canTransactions && {
      title: "تراکنشات",
      icon: "solar:card-transfer-broken",
      color: "warning",
      key: "transaction",
      link: "/transactions",
    },
    auth.isAdmin &&
      permissions.canCategories && {
        title: "دسته بندی ها",
        icon: "solar:box-minimalistic-broken",
        color: "primary",
        key: "category",
        link: "/categories",
      },
    permissions.canPayments && {
      title: "پرداخت ها",
      icon: "solar:hand-money-broken",
      color: "secondary",
      key: "payment",
      link: "/payments",
    },
    auth.isAdmin &&
      permissions.canBulkSMSs && {
        title: "پیامک های انبوه",
        icon: "solar:dialog-broken",
        color: "success",
        key: "bulkSMS",
        link: "/bulk-sms",
      },
    auth.isAdmin && {
      title: "اعلان ها",
      icon: "solar:bell-broken",
      color: "info",
      key: "notification",
      link: "/notifications",
    },
  ].filter(Boolean);
  return (
    <div className="col-12">
      <div className="card radius-8 border-0">
        <div className="row h-100 g-0">
          {items.map((item) => (
            <div
              key={item.key}
              className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2 p-0 m-0"
            >
              <Link
                to={`/${convertRoleToPath(auth.role)}${item.link}`}
                className="card-body p-24 h-100 d-flex flex-column justify-content-center border border-top-0"
              >
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                  <div>
                    <span
                      className={`mb-12 w-44-px h-44-px text-${item.color}-600 border border-${item.color}-light-white flex-shrink-0 d-flex justify-content-center align-items-center radius-8 h6 mb-12`}
                    >
                      <Icon icon={item.icon} className="icon" />
                    </span>
                    <span className="mb-1 fw-medium text-secondary-light text-md">
                      {item.title}
                    </span>
                    <h6 className={`fw-semibold text-${item.color}-light mb-1`}>
                      {numberSeparate(overview.count[item.key])}
                    </h6>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProductStatistics = () => {
  const { overview } = useContext(dashboardUserContext);

  const series = productTypeItems?.map(
    (type) => overview?.count[type.toLowerCase()]
  );
  const colors = productTypeItems?.map(() => generateRandomHexColor());
  const labels = productTypeItems?.map((type) =>
    convertProductTypeToText(type)
  );

  let chartOptions = {
    colors,
    labels,
    legend: {
      show: false,
    },
    chart: {
      type: "donut",
      height: 230,
      sparkline: {
        enabled: true, // Remove whitespace
      },
      margin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    stroke: {
      width: 0,
    },
    dataLabels: {
      enabled: false,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  return (
    <div className="col-xxl-3 col-lg-6">
      <div className="card h-100 radius-8 border-0">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-2 fw-bold text-lg">آمار محصولات</h6>
          </div>
          <div className="position-relative">
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="donut"
              height={230}
              id="statisticsDonutChart"
              className="mt-36 flex-grow-1 apexcharts-tooltip-z-none title-style circle-none"
            />
          </div>
          <ul className="mt-3 gap-3">
            {labels.map((item, index) => (
              <li
                className="d-flex align-items-center justify-content-between gap-2"
                key={item}
              >
                <span
                  className="w-12-px h-12-px radius-2"
                  style={{ backgroundColor: colors[index] }}
                />
                <span className="text-secondary-light text-sm fw-normal w-75">
                  {item}
                </span>
                <span className="text-primary-light fw-bold ms-2">
                  {series[index]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const RecentOrders = () => {
  const auth = useSelector((state) => state.auth);

  const { overview } = useContext(dashboardUserContext);

  return (
    <div className="col-12">
      <div className="card h-100">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
            <h6 className="mb-2 fw-bold text-lg mb-0">سفارشات آخر</h6>
            <Link
              to={`/${convertRoleToPath(auth.role)}/orders`}
              className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
            >
              مشاهده همه
              <i className="ri-arrow-left-double-line text-xl d-flex line-height-1" />
            </Link>
          </div>
          {isEmpty(overview?.orders) ? (
            <EmptyData />
          ) : (
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">محصول</th>
                    {auth.isAdmin && (
                      <>
                        <th scope="col">کاربر</th>
                        <th scope="col">شماره همراه</th>
                      </>
                    )}
                    <th scope="col">
                      مبلغ <Currency />
                    </th>
                    <th scope="col">تاریخ ثبت</th>
                    <th scope="col">وضعیت فاکتور</th>
                    <th scope="col">وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.orders?.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <Link
                          to={`/${convertRoleToPath(auth.role)}/product/${
                            item?.product?.original
                          }/product`}
                        >
                          <div className="d-flex align-items-center">
                            <UserAvatar
                              className="ms-2"
                              {...(item?.product?.picture
                                ? { image: item?.product?.picture }
                                : {
                                    icon: "solar:box-broken",
                                  })}
                            />
                            <div className="flex-grow-1">
                              <span className="text-md mb-0 fw-normal text-secondary-light">
                                {item?.product?.title}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </td>
                      {auth.isAdmin && (
                        <>
                          <td>
                            <div className="d-flex align-items-center">
                              <UserAvatar
                                className="ms-2"
                                {...(item?.user?.avatar
                                  ? { image: item?.user?.avatar }
                                  : {
                                      icon: "solar:order-broken",
                                    })}
                              />
                              <div className="flex-grow-1">
                                <span className="text-md mb-0 fw-normal text-secondary-light">
                                  {item?.user?.firstName} {item?.user?.lastName}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{item?.user?.phoneNumber}</td>
                        </>
                      )}
                      <td>
                        <ConvertCurrency
                          price={item?.invoice?.amount}
                          pending
                        />
                      </td>
                      <td>
                        {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                      </td>
                      <td>
                        <Badge
                          color={convertInvoiceStatusToColor(
                            item?.invoice?.status
                          )}
                        >
                          {convertInvoiceStatusToText(item?.invoice?.status)}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={convertOrderStatusToColor(item.status)}>
                          {convertOrderStatusToText(item.status)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecentProducts = () => {
  const auth = useSelector((state) => state.auth);

  const { overview } = useContext(dashboardUserContext);

  return (
    <div className="col-xxl-9 col-lg-6">
      <div className="card h-100">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
            <h6 className="mb-2 fw-bold text-lg mb-0">محصولات آخر</h6>
            <Link
              to={`/${convertRoleToPath(auth.role)}/products`}
              className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
            >
              مشاهده همه
              <i className="ri-arrow-left-double-line text-xl d-flex line-height-1" />
            </Link>
          </div>
          {isEmpty(overview?.products) ? (
            <EmptyData />
          ) : (
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">محصول</th>
                    <th scope="col">کد</th>
                    <th scope="col">دسته بندی</th>
                    <th scope="col">نوع</th>
                    <th scope="col">
                      قیمت <Currency />
                    </th>
                    <th scope="col">
                      دوره تمدید <Badge color="primary">روز</Badge>
                    </th>
                    <th scope="col">تاریخ ایجاد</th>
                    <th scope="col">تاریخ آخرین ویرایش</th>
                    <th scope="col">وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {overview?.products?.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <Link
                          to={`/${convertRoleToPath(auth.role)}/product/${
                            item?._id
                          }/product`}
                        >
                          <div className="d-flex align-items-center">
                            <UserAvatar
                              className="ms-2"
                              {...(item?.picture
                                ? { image: item?.picture }
                                : {
                                    icon: "solar:box-broken",
                                  })}
                            />
                            <div className="flex-grow-1">
                              <span className="text-md mb-0 fw-normal text-secondary-light">
                                {item.title}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </td>
                      <td>{item.code}</td>
                      <td>
                        {item.category?.parent && (
                          <>
                            <Link
                              to={`/${convertRoleToPath(auth.role)}/category/${
                                item.category?.parent?._id
                              }/category`}
                            >
                              {item.category?.parent?.title}
                            </Link>
                            {" / "}
                          </>
                        )}
                        <Link
                          to={`/${convertRoleToPath(auth.role)}/product/${
                            item.category?._id
                          }/category`}
                        >
                          {item.category?.title}
                        </Link>
                      </td>
                      <td>{convertProductTypeToText(item.type)}</td>
                      <td>
                        <ConvertCurrency price={item.price} />
                      </td>
                      <td>{item.renewalPeriod}</td>
                      <td>
                        {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                      </td>
                      <td>
                        {convertDate(item.updatedAt, "YYYY/MM/DD - HH:mm")}
                      </td>
                      <td>
                        <Badge color={item.isActive ? "success" : "danger"}>
                          {item.isActive ? "فعال" : "غیرفعال"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecentUsers = () => {
  const auth = useSelector((state) => state.auth);

  const { overview } = useContext(dashboardUserContext);

  return (
    <div className="col-xxl-9 col-lg-6">
      <div className="card h-100">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
            <h6 className="mb-2 fw-bold text-lg mb-0">ثبت نامی های آخر</h6>
            <Link
              to={`/${convertRoleToPath(auth.role)}/users`}
              className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
            >
              مشاهده همه
              <i className="ri-arrow-left-double-line text-xl d-flex line-height-1" />
            </Link>
          </div>
          {isEmpty(overview?.users) ? (
            <EmptyData />
          ) : (
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">کاربر</th>
                    <th scope="col">شماره همراه</th>
                    <th scope="col">کد ملی</th>
                    <th scope="col">تاریخ ثبت نام</th>
                    <th scope="col">وضعیت</th>
                  </tr>
                </thead>
                <tbody>
                  {overview?.users?.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <UserAvatar
                            className="ms-2"
                            {...(item?.avatar
                              ? { image: item?.avatar }
                              : {
                                  icon: "solar:user-broken",
                                })}
                          />
                          <div className="flex-grow-1">
                            <span className="text-md mb-0 fw-normal text-secondary-light">
                              {item.firstName} {item.lastName}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item.phoneNumber}
                          <VerifiedBadge
                            isVerified={item?.phoneNumberVerified}
                          />
                        </span>
                      </td>
                      <td>{item.nationalNumber}</td>
                      <td>
                        {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                      </td>
                      <td>
                        <Badge color={item.isConfirm ? "success" : "danger"}>
                          {item.isConfirm ? "فعال" : "غیرفعال"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UserStatistics = () => {
  const { overview } = useContext(dashboardUserContext);

  const series = userRoleItems?.map(
    (type) => overview?.count[type.toLowerCase()]
  );
  const colors = userRoleItems?.map(() => generateRandomHexColor());
  const labels = userRoleItems?.map((role) => convertRoleToText(role));

  let chartOptions = {
    colors,
    labels,
    legend: {
      show: false,
    },
    chart: {
      type: "donut",
      height: 230,
      sparkline: {
        enabled: true, // Remove whitespace
      },
      margin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    stroke: {
      width: 0,
    },
    dataLabels: {
      enabled: false,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  return (
    <div className="col-xxl-3 col-lg-6">
      <div className="card h-100 radius-8 border-0">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-2 fw-bold text-lg">آمار کاربران</h6>
          </div>
          <div className="position-relative">
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="donut"
              height={230}
              id="statisticsDonutChart"
              className="mt-36 flex-grow-1 apexcharts-tooltip-z-none title-style circle-none"
            />
          </div>
          <ul className="mt-3 gap-3">
            {labels.map((item, index) => (
              <li
                className="d-flex align-items-center justify-content-between gap-2"
                key={item}
              >
                <span
                  className="w-12-px h-12-px radius-2"
                  style={{ backgroundColor: colors[index] }}
                />
                <span className="text-secondary-light text-sm fw-normal w-75">
                  {item}
                </span>
                <span className="text-primary-light fw-bold ms-2">
                  {series[index]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const RecentTransactions = () => {
  const auth = useSelector((state) => state.auth);

  const { overview } = useContext(dashboardUserContext);

  return (
    <div className="col-12">
      <div className="card h-100">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
            <h6 className="mb-2 fw-bold text-lg mb-0">تراکنشات آخر</h6>
            <Link
              to={`/${convertRoleToPath(auth.role)}/transactions`}
              className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
            >
              مشاهده همه
              <i className="ri-arrow-left-double-line text-xl d-flex line-height-1" />
            </Link>
          </div>
          {isEmpty(overview?.transactions) ? (
            <EmptyData />
          ) : (
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    {auth.isAdmin && <th scope="col">کاربر</th>}
                    <th scope="col">
                      مبلغ <Currency />
                    </th>
                    <th scope="col">
                      موجودی بعد از عملیات <Currency />
                    </th>
                    <th scope="col">نوع</th>
                    <th scope="col">تاریخ</th>
                  </tr>
                </thead>
                <tbody>
                  {overview?.transactions?.map((item, index) => {
                    const color = convertTransactionTypeToColor(item.type);
                    return (
                      <tr>
                        <td>{index + 1}</td>
                        {auth.isAdmin && (
                          <td>
                            <div className="d-flex align-items-center">
                              <UserAvatar
                                className="ms-2"
                                {...(item?.user?.avatar
                                  ? { image: item?.user?.avatar }
                                  : {
                                      icon: "solar:user-broken",
                                    })}
                              />
                              <div className="flex-grow-1">
                                <span className="text-md mb-0 fw-normal text-secondary-light">
                                  {item?.user?.firstName} {item?.user?.lastName}
                                </span>
                              </div>
                            </div>
                          </td>
                        )}
                        <td>
                          <ConvertCurrency price={item.amount} />
                        </td>
                        <td>
                          <Badge className="badge-dim text-sm" color={color}>
                            <ConvertCurrency price={item.walletBalance} />
                            <i
                              className={convertTransactionTypeToIcon(
                                item.type
                              )}
                            />
                          </Badge>
                        </td>
                        <td>
                          <Badge className="badge-dim text-sm" color={color}>
                            {convertTransactionTypeToText(item.type)}
                          </Badge>
                        </td>
                        <td>
                          {convertDate(
                            item.createdAt,
                            "jYYYY/jMM/jDD - HH:mm:ss"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecentInvoices = () => {
  const auth = useSelector((state) => state.auth);

  const { overview } = useContext(dashboardUserContext);

  return (
    <div className="col-12">
      <div className="card h-100">
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
            <h6 className="mb-2 fw-bold text-lg mb-0">فاکتورهای آخر</h6>
            <Link
              to={`/${convertRoleToPath(auth.role)}/invoices`}
              className="text-primary-600 hover-text-primary d-flex align-items-center gap-1"
            >
              مشاهده همه
              <i className="ri-arrow-left-double-line text-xl d-flex line-height-1" />
            </Link>
          </div>
          {isEmpty(overview?.invoices) ? (
            <EmptyData />
          ) : (
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">شماره</th>
                    {auth.isAdmin && (
                      <>
                        <th scope="col">کاربر</th>
                        <th scope="col">شماره همراه</th>
                      </>
                    )}
                    <th scope="col">
                      مبلغ <Currency />
                    </th>
                    <th scope="col">وضعیت</th>
                    <th scope="col">تاریخ انقضا</th>
                    <th scope="col">تاریخ ایجاد</th>
                  </tr>
                </thead>
                <tbody>
                  {overview?.invoices?.map((item) => {
                    const isPay =
                      item.amount > 0 && item.status === invoiceStatusItems[0];

                    const prefixClass = isPay ? "bg-warning-100" : "";
                    return (
                      <tr key={item._id}>
                        <td className={prefixClass}>{item?.number}</td>
                        {auth.isAdmin && (
                          <>
                            <td className={prefixClass}>
                              <div className="d-flex align-items-center">
                                <UserAvatar
                                  className="ms-2"
                                  {...(item?.user?.avatar
                                    ? { image: item?.user?.avatar }
                                    : {
                                        icon: "solar:user-broken",
                                      })}
                                />
                                <div className="flex-grow-1">
                                  <span className="text-md mb-0 fw-normal text-secondary-light">
                                    {item?.user?.firstName}{" "}
                                    {item?.user?.lastName}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className={prefixClass}>
                              {item?.user?.phoneNumber}
                            </td>
                          </>
                        )}
                        <td className={prefixClass}>
                          <ConvertCurrency price={item.amount} pending />
                        </td>
                        <td className={prefixClass}>
                          <Badge
                            color={convertInvoiceStatusToColor(item.status)}
                          >
                            {convertInvoiceStatusToText(item.status)}
                          </Badge>
                        </td>
                        <td className={prefixClass}>
                          {convertDate(item.expiredAt)}
                        </td>
                        <td className={prefixClass}>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
